import React, { useEffect, useState } from 'react';
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, Thermometer, MapPin, RefreshCw } from 'lucide-react';

// WMO weather code → { label, Icon, color }
function getWeatherInfo(code) {
  if (code === 0)            return { label: 'Sonnig',           Icon: Sun,             color: '#FBBF24' };
  if (code <= 2)             return { label: 'Teils bewölkt',    Icon: Cloud,           color: '#9CA3AF' };
  if (code === 3)            return { label: 'Bedeckt',          Icon: Cloud,           color: '#6B7280' };
  if (code <= 48)            return { label: 'Neblig',           Icon: Wind,            color: '#9CA3AF' };
  if (code <= 55)            return { label: 'Nieselregen',      Icon: CloudRain,       color: '#60A5FA' };
  if (code <= 67)            return { label: 'Regen',            Icon: CloudRain,       color: '#3B82F6' };
  if (code <= 77)            return { label: 'Schnee',           Icon: CloudSnow,       color: '#BAE6FD' };
  if (code <= 82)            return { label: 'Regenschauer',     Icon: CloudRain,       color: '#60A5FA' };
  if (code <= 99)            return { label: 'Gewitter',         Icon: CloudLightning,  color: '#A78BFA' };
  return                            { label: 'Unbekannt',        Icon: Cloud,           color: '#9CA3AF' };
}

const CACHE_KEY   = 'gm_weather_cache';
const CACHE_TTL   = 30 * 60 * 1000; // 30 minutes

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts < CACHE_TTL) return data;
  } catch {}
  return null;
}

function saveCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
}

export default function WeatherWidget() {
  const [weather, setWeather]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [denied, setDenied]     = useState(false);
  const [error, setError]       = useState(false);

  const fetchWeather = async (lat, lon) => {
    try {
      const [meteoRes, geoRes] = await Promise.all([
        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m&wind_speed_unit=kmh&timezone=auto`),
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&accept-language=de`, {
          headers: { 'Accept-Language': 'de' },
        }),
      ]);
      const meteo = await meteoRes.json();
      const geo   = await geoRes.json();

      const city = geo?.address?.city || geo?.address?.town || geo?.address?.village || geo?.address?.county || 'Dein Standort';
      const cur  = meteo.current;
      const data = {
        city,
        temp:       Math.round(cur.temperature_2m),
        feels_like: Math.round(cur.apparent_temperature),
        humidity:   cur.relative_humidity_2m,
        wind:       Math.round(cur.wind_speed_10m),
        code:       cur.weather_code,
      };
      saveCache(data);
      setWeather(data);
    } catch {
      setError(true);
    }
    setLoading(false);
  };

  const requestLocation = () => {
    setLoading(true);
    setDenied(false);
    setError(false);
    if (!navigator.geolocation) { setError(true); setLoading(false); return; }
    navigator.geolocation.getCurrentPosition(
      pos => fetchWeather(pos.coords.latitude, pos.coords.longitude),
      () => { setDenied(true); setLoading(false); },
      { timeout: 10000 }
    );
  };

  useEffect(() => {
    const cached = loadCache();
    if (cached) { setWeather(cached); setLoading(false); return; }
    requestLocation();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="card-grow p-4 flex items-center gap-3" data-testid="weather-widget-loading">
      <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
      <span className="text-sm text-emerald-100/50">Wetter wird geladen...</span>
    </div>
  );

  if (denied) return (
    <div className="card-grow p-4 flex items-center justify-between gap-3" data-testid="weather-widget-denied">
      <div className="flex items-center gap-2">
        <MapPin size={16} className="text-emerald-400/50" />
        <div>
          <p className="text-sm font-medium text-emerald-100">Wetter nicht verfügbar</p>
          <p className="text-xs text-emerald-100/40">Standortzugriff verweigert</p>
        </div>
      </div>
      <button onClick={requestLocation} className="p-1.5 rounded-lg text-emerald-100/30 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors">
        <RefreshCw size={14} />
      </button>
    </div>
  );

  if (error || !weather) return (
    <div className="card-grow p-4 flex items-center justify-between gap-3" data-testid="weather-widget-error">
      <div className="flex items-center gap-2">
        <Cloud size={16} className="text-emerald-400/40" />
        <p className="text-sm text-emerald-100/40">Wetter konnte nicht geladen werden</p>
      </div>
      <button onClick={requestLocation} className="p-1.5 rounded-lg text-emerald-100/30 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors">
        <RefreshCw size={14} />
      </button>
    </div>
  );

  const { Icon, label, color } = getWeatherInfo(weather.code);

  return (
    <div className="card-grow p-4 relative overflow-hidden" data-testid="weather-widget">
      {/* Subtle background glow */}
      <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-5 pointer-events-none"
        style={{ background: color, filter: 'blur(40px)', transform: 'translate(30%, -30%)' }} />

      <div className="flex items-start justify-between relative z-10">
        <div className="flex-1">
          {/* Location */}
          <div className="flex items-center gap-1.5 mb-2">
            <MapPin size={12} className="text-emerald-400" />
            <span className="text-xs text-emerald-100/60 font-medium">{weather.city}</span>
            <button
              onClick={requestLocation}
              className="ml-1 p-0.5 text-emerald-100/20 hover:text-emerald-400 transition-colors"
              title="Aktualisieren"
              data-testid="weather-refresh-btn"
            >
              <RefreshCw size={10} />
            </button>
          </div>

          {/* Temp */}
          <div className="flex items-end gap-3">
            <p className="font-heading text-4xl font-bold text-emerald-50 leading-none">
              {weather.temp}°C
            </p>
            <div className="pb-0.5">
              <p className="text-sm font-medium" style={{ color }}>{label}</p>
              <p className="text-xs text-emerald-100/40">Gefühlt {weather.feels_like}°C</p>
            </div>
          </div>

          {/* Details */}
          <div className="flex items-center gap-4 mt-3">
            <div className="flex items-center gap-1.5">
              <Droplets size={12} className="text-blue-400" />
              <span className="text-xs text-emerald-100/50">{weather.humidity}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Wind size={12} className="text-emerald-400/60" />
              <span className="text-xs text-emerald-100/50">{weather.wind} km/h</span>
            </div>
          </div>
        </div>

        {/* Big Icon */}
        <div className="flex-shrink-0">
          <Icon size={52} style={{ color, opacity: 0.85 }} />
        </div>
      </div>
    </div>
  );
}
